import React, { PropsWithChildren, useCallback, useContext, useMemo } from 'react'
import cn from 'clsx'
import { connect } from 'react-redux'

import Tooltip from 'components/Luxkit/Tooltip'
import config from 'constants/config'
import { hasIncludedGuestsExceeded } from 'lib/checkout/cartReservationUtils'
import AdditionalGuestsPopup from 'components/Common/AdditionalGuestsPopup/AdditionalGuestsPopup'
import LinePlaneFlyIcon from 'components/Luxkit/Icons/line/LinePlaneFlyIcon'
import SolidExclamationCircleIcon from 'components/Luxkit/Icons/solid/SolidExclamationCircleIcon'
import {
  Root,
  CacheOffText,
} from '../styles'
import { decodeSearchParams } from 'lib/search/searchUtils'
import PriceRowTaxesAndFees from 'components/Luxkit/PricePoints/PriceRowTaxesAndFees'
import { OFFER_TYPE_LAST_MINUTE } from 'constants/offer'
import styled from 'styled-components'
import { rem } from 'polished'
import CaptionTextBlock from 'components/Luxkit/TextBlocks/CaptionTextBlock'
import PriceRowPriceCaption from 'components/Luxkit/PricePoints/PriceRowPriceCaption'
import PriceRowPrice from 'components/Luxkit/PricePoints/PriceRowPrice'
import PriceRowValueDiscountWithCaption from 'components/Luxkit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import CruisesDiscountTsAndCsModal from 'components/Cruises/CruisesDiscountTsAndCsModal'
import ModalContext from 'contexts/ModalContext'
import PriceRowCaption from 'components/Luxkit/PricePoints/PriceRowCaption'
import useCruiseOfferView from 'hooks/Cruise/useCruiseOfferView'

const LineText = styled(CaptionTextBlock)`
  margin-top: ${rem(6)};
`

interface Props {
  className?: string;
  durationLabel: string;
  flightCacheOff?: boolean;
  hasClause?: boolean;
  infoBar?: boolean;
  infoBarMedium?: boolean;
  infoBarMini?: boolean;
  isFlightPrice?: boolean;
  label: string;
  offerSaleUnit: string;
  price: number;
  roomOccupancy?: string;
  type: string;
  shouldShowValue?: boolean;
  showFlights?: boolean;
  value: number;
  originAirport?: App.Airport;
  noFlightsFromOrigin?: boolean;
  discountPercent: number;
  roomType?: App.PackageRoomType;
  searchLocation?: any;
  maxChildAge?: number;
  maxInfantAge?: number;
  roomPolicyDescription?: string;
  availablePackage?: App.Package;
  offer: App.TourOffer | App.TourOfferSummary;
  showDiscountPill?: boolean;
  highestDiscountPercent?: number;
  isCruise?: boolean;
}

function OfferPrice({
  durationLabel,
  hasClause,
  infoBar,
  label,
  offerSaleUnit,
  price,
  roomOccupancy,
  shouldShowValue,
  showFlights,
  flightCacheOff,
  isFlightPrice,
  value,
  type,
  discountPercent,
  children,
  originAirport,
  noFlightsFromOrigin,
  roomType,
  searchLocation,
  maxChildAge,
  maxInfantAge,
  roomPolicyDescription,
  availablePackage,
  offer,
  showDiscountPill,
  highestDiscountPercent,
  isCruise,
}: PropsWithChildren<Props>) {
  const { lowestPrice } = useCruiseOfferView({ offer })
  const showIncludedGuestsExceeded = useMemo(() => {
    if (!config.SEARCH_ENABLED || !roomType || availablePackage?.roomRate?.extraGuestSurcharges.length > 0) {
      return false
    }

    const filters = decodeSearchParams(searchLocation, maxChildAge, maxInfantAge)
    return filters.rooms.some(room => hasIncludedGuestsExceeded(room, availablePackage, offer))
  }, [roomType, availablePackage, searchLocation, maxChildAge, maxInfantAge, offer])

  const hotelPrice = price

  const showModal = useContext(ModalContext)

  const openDiscountTermsAndConditionsModal = useCallback(() => {
    showModal(<CruisesDiscountTsAndCsModal />)
  }, [showModal])

  const discountPercentage = useMemo(() => {
    if (showDiscountPill && shouldShowValue && value >= price) {
      return getValueOffPercent(value, price)
    }
  }, [showDiscountPill, shouldShowValue, value, price])

  return (
    <Root data-testid={`${isFlightPrice ? 'flights-' : ''}pricing`}>
      {showFlights && <CaptionTextBlock
        variant="large"
        format="titlecase"
        weight="bold"
        startIcon={isFlightPrice && <LinePlaneFlyIcon />}
      >
        {label}
      </CaptionTextBlock>}
      {isFlightPrice && flightCacheOff &&
        <CacheOffText
          className={cn({ short: !roomOccupancy, infobar: infoBar })}
          variant="medium"
          colour="neutral-three"
        >
          {noFlightsFromOrigin && originAirport &&
            `No flights available from ${originAirport.name}. Please select another airport`
          }
          {(!noFlightsFromOrigin || !originAirport) &&
            'Add flights to the hotel package. Select from all major airlines at the next step.'
          }
        </CacheOffText>
      }
      {(!isFlightPrice || !flightCacheOff) && <>
        {hotelPrice && <>
          {isCruise && offer.lowestPricePackage?.duration && <PriceRowPriceCaption>
            {offer.lowestPricePackage.duration - 1} nights from
          </PriceRowPriceCaption>}
          {(!isCruise || !offer.lowestPricePackage?.duration) && <PriceRowPriceCaption>
            {durationLabel} from
          </PriceRowPriceCaption>}
          <PriceRowPrice
            size="L"
            price={Math.ceil(hotelPrice)}
            saleUnit={`${offerSaleUnit}${hasClause ? '*' : ''}`}
          />
        </>}
        {isCruise && <>
          {shouldShowValue && value >= price &&
            <PriceRowValueDiscountWithCaption
              size="M"
              data-testid="value-box"
              originalValue={Math.ceil(value)}
              discountPercentage={highestDiscountPercent}
              onInfoIconClick={openDiscountTermsAndConditionsModal}
            />
          }
          {!isFlightPrice && roomOccupancy && !lowestPrice?.secondaryPrice && <PriceRowCaption>Twin Share</PriceRowCaption>}
          {isFlightPrice && <PriceRowCaption>Two Adults</PriceRowCaption>}
        </>}
        <PriceRowTaxesAndFees />
        {!isCruise && !isFlightPrice && roomOccupancy && <PriceRowCaption>{roomOccupancy}</PriceRowCaption>}
        {!isCruise && isFlightPrice && <PriceRowCaption>Two Adults</PriceRowCaption>}
      </>}
      {children}
      {showIncludedGuestsExceeded && !(isFlightPrice && flightCacheOff) && (
        <AdditionalGuestsPopup
          complex={false}
          description="+ Extra guest surcharge"
          modalContent={roomPolicyDescription}
        />
      )}
      {!isCruise && shouldShowValue && value >= price &&
        <PriceRowValueDiscountWithCaption
          size="M"
          data-testid="value-box"
          originalValue={Math.ceil(value)}
          discountPercentage={discountPercentage}
          onInfoIconClick={openDiscountTermsAndConditionsModal}
        />
      }
      {discountPercent > 0 && type === OFFER_TYPE_LAST_MINUTE &&
        <LineText
          variant="medium"
          weight="bold"
          colour="neutral-three"
          data-testid="value-box"
          endIcon={<Tooltip
            description="Discount calculated on the best rate available for this package as reported to us by the property. Rate excludes the value of any package inclusions – they’re our gift to you."
          >
            <SolidExclamationCircleIcon colour="primary" />
          </Tooltip>}
        >
          {discountPercent}% off best available rate
        </LineText>
      }
    </Root>
  )
}

function mapStateToProps(state: App.State) {
  return {
    searchLocation: state.router.location.search,
  }
}

export default connect(mapStateToProps)(OfferPrice)
